import { startCase, upperFirst } from 'lodash';
import React from 'react';
import { gql, graphql } from 'react-apollo';
import { toast } from 'react-toastify';
import graphqlWithoutCache from '../graphqlWithoutCache';
import Form from './Form';
import FormNavigation from './formLib/formNavigation';
import jsonSchemaToGqlQuery from '../GqlCmsConfigLib/jsonSchemaToGqlQuery';
import getFieldsToIgnore from '../GqlCmsConfigLib/getFieldsToIgnore';
import getCRUDSchemaFromResource from '../GqlCmsConfigLib/getCRUDSchemaFromResource';
import removeTypename from '../removeTypename';
import nullToUndefined from '../formLib/nullToUndefined';
import undefinedToNull from '../formLib/undefinedToNull';
import mergeSplittedFields from '../formLib/mergeSplittedFields';
import joinFieldArraysIntoOne from '../formLib/joinFieldArraysIntoOne';
import alertFirstGqlMsg from '../alertFirstGqlMsg';
import transformErrors from '../formLib/transformErrors';

function Update(props) {
  const { config, resource, update, data, history, duplicate, createTemplate } = props;
  const dirtyFormData = data[resource.crudMapping.readOne];
  const purifiedFormData = nullToUndefined(removeTypename(dirtyFormData));
  if (data.loading) {
    return null;
  }

  // changing roles from array to the string value it contains because,
  //  for now, the user can only have one role,
  // and we must do this for the form to work, as it is expecting a string and not an array
  if (purifiedFormData.roles && purifiedFormData.roles.length) {
    purifiedFormData.roles = purifiedFormData.roles[0];
  }

  if (purifiedFormData.roles && purifiedFormData.roles.length === 0) {
    purifiedFormData.roles = 'USER';
  }

  let temporaryFormData = purifiedFormData || {};

  const resourceName = resource.displayName || resource.name;
  if (resourceName === 'company') {
    purifiedFormData.curatorLandingLink = `https://encouragex.com/curators/${purifiedFormData.slug}`;
  }

  const updateSchema = getCRUDSchemaFromResource({
    config,
    resource,
    crudType: 'update',
  });

  const { displayName } = updateSchema.jsonSchema;
  const sections = getFieldsToIgnore(updateSchema.jsonSchema);
  const onSubmit = async ({ formData }) => {
    let input = undefinedToNull(formData);
    delete input.curatorLandingLink;
    if (updateSchema.uiSchema.updatedAt) {
      input.updatedAt = new Date();
    }
    sections.forEach(field => delete input[field]);
    if (resource.isPublishable) {
      input.published = purifiedFormData.published;
    }
    if (temporaryFormData.published) {
      input.published = true;
    }
    input = mergeSplittedFields(input, updateSchema.jsonSchema);
    input = joinFieldArraysIntoOne(input, updateSchema.jsonSchema);
    try {
      await update({ variables: { input } });
      history.push(`/${resourceName}`);
      toast.success('Update Success');
    } catch (e) {
      alertFirstGqlMsg(e);
    }
  };

  const onPublish = async () => {
    let input = temporaryFormData.published ? purifiedFormData : temporaryFormData;
    delete input.curatorLandingLink;
    input = mergeSplittedFields(input, updateSchema.jsonSchema);
    input = joinFieldArraysIntoOne(input, updateSchema.jsonSchema);
    input.published = !temporaryFormData.published;
    try {
      await update({ variables: { input } });
      history.replace(history.location.pathname);
      toast.success('Update Success');
    } catch (e) {
      alertFirstGqlMsg(e);
    }
  };

  const onDuplicate = async () => {
    const uniqKeyValue = props.match.params[resource.uniqKey];
    try {
      const {
        data: { [`duplicate${upperFirst(resource.name)}`]: newUniq },
      } = await duplicate({
        variables: {
          [resource.uniqKey]: uniqKeyValue,
        },
      });
      const newRoute = history.location.pathname.replace(uniqKeyValue, newUniq);
      toast.success('Duplicated successfully');
      await new Promise((resolve) => {
        setTimeout(() => {
          resolve(true);
        }, 1000);
      });
      window.open(newRoute, '_blank');
    } catch (error) {
      console.log(error);
      toast.error(error.message);
    }
  };

  const onCancel = () => {
    history.push(`/${resourceName}`);
  };

  const extraProps = {};

  if (resource.crudMapping.createTemplate) {
    const onCreateTemplate = async () => {
      let input = undefinedToNull(purifiedFormData);
      delete input.published;
      delete input.lifeEventCategoryIds;
      delete input.showTBYB;
      delete input.dropIds;
      delete input.stickyDropIds;
      sections.forEach(field => delete input[field]);
      input = mergeSplittedFields(input, updateSchema.jsonSchema);
      input = joinFieldArraysIntoOne(input, updateSchema.jsonSchema);
      try {
        await createTemplate({ variables: { input } });
        history.push(`/sample${upperFirst(resourceName)}`);
        toast.success('Create Template Success');
      } catch (e) {
        alertFirstGqlMsg(e);
      }
    };

    extraProps.onCreateTemplate = onCreateTemplate;
  }

  const openUiSchema = {
    'ui:disabled': true,
    ...updateSchema.uiSchema,
  };

  return (
    <div className="form-page-wrapper">
      <div className="form-container">
        <h1>Update {startCase(displayName || resourceName)}</h1>
        <Form
          openPlatform={purifiedFormData.openPlatform}
          jsonSchemaFormExtensions={config.jsonSchemaFormExtensions}
          schema={updateSchema.jsonSchema}
          uiSchema={purifiedFormData.openPlatform ? openUiSchema : updateSchema.uiSchema}
          formData={purifiedFormData}
          noHtml5Validate
          onSubmit={onSubmit}
          onCancel={onCancel}
          isPublishable={resource.isPublishable}
          isDuplicable={resource.isDuplicable}
          onPublish={onPublish}
          onDuplicate={onDuplicate}
          isPublished={purifiedFormData.published}
          transformErrors={transformErrors}
          onChange={(ev) => {
            temporaryFormData = { ...temporaryFormData, ...ev.formData };
          }}
          {...extraProps}
        />
      </div>
      {sections && sections.length > 0 && (
        <FormNavigation sections={sections} jsonSchema={updateSchema.jsonSchema} />
      )}
    </div>
  );
}

function UpdateWithData(props) {
  const { config, resource } = props;
  const { crudMapping, uniqKey } = resource;
  const updateSchema = getCRUDSchemaFromResource({
    config,
    resource,
    crudType: 'update',
  });

  let Component = Update;
  const fieldsQuery = jsonSchemaToGqlQuery(updateSchema.jsonSchema).replace('curatorLandingLink', '');
  const ReadOneQuery = gql`
  query ${crudMapping.readOne}($${uniqKey}: String!) {
    ${crudMapping.readOne}(${uniqKey}: $${uniqKey}) ${fieldsQuery}
  }
  `;
  const uniqKeyValue = props.match.params[uniqKey];
  Component = graphqlWithoutCache(ReadOneQuery, {
    options: { variables: { [uniqKey]: uniqKeyValue } },
  })(Component);

  const UpdateQuery = gql`
    mutation ${crudMapping.update}($input: ${upperFirst(
  updateSchema.inputName || `${resource.name}Input`,
)}!) {
      ${crudMapping.update}(${resource.name} : $input, ${uniqKey}: "${uniqKeyValue}")
    }
  `;
  Component = graphql(UpdateQuery, { name: 'update' })(Component);

  const DuplicateMutation = gql`
    mutation duplicate${upperFirst(resource.name)}($${uniqKey}: String!) {
      duplicate${upperFirst(resource.name)}(${uniqKey}: $${uniqKey})
    }
  `;
  Component = graphql(DuplicateMutation, { name: 'duplicate' })(Component);

  if (crudMapping.createTemplate) {
    const CreateTemplateMutation = gql`
      mutation ${crudMapping.createTemplate}($input: Sample${upperFirst(`${resource.name}Input`)}!) {
        ${crudMapping.createTemplate}(sample${upperFirst(resource.name)} : $input)
      }
    `;
    Component = graphql(CreateTemplateMutation, { name: 'createTemplate' })(Component);
  }

  return <Component {...props} />;
}

export default UpdateWithData;



// WEBPACK FOOTER //
// ./src/byob-cms/src/AppLib/Update.js